import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Globe, ChevronDown } from 'lucide-react';

const languages = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'es-CL', name: 'Español (Chile)', flag: '🇨🇱' },
];

// Two-letter codes for the mobile trigger, kept separate from `languages` so this
// file's only Spanish characters stay on the untouched endonym line above.
const shortCodes: Record<string, string> = { en: 'EN', 'es-CL': 'ES' };

export function LanguageSelector() {
  const { i18n } = useTranslation();
  const { userProfile } = useAuth();

  const handleLanguageChange = (langCode: string) => {
    i18n.changeLanguage(langCode);
    // Persist to DB (fire-and-forget)
    if (userProfile?.id) {
      supabase
        .from('users')
        .update({ language: langCode, updated_at: new Date().toISOString() })
        .eq('id', userProfile.id)
        .then();
    }
  };

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5 min-h-[44px] min-w-[44px] px-3"
        >
          <Globe className="h-4 w-4" />
          <span className="hidden sm:inline">
            {currentLanguage.flag} {currentLanguage.name}
          </span>
          <span className="sm:hidden">
            {currentLanguage.flag} {shortCodes[currentLanguage.code]}
          </span>
          <ChevronDown className="h-3 w-3 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {languages.map((language) => (
          <DropdownMenuItem
            key={language.code}
            onClick={() => handleLanguageChange(language.code)}
            className="cursor-pointer"
          >
            <span className="mr-2">{language.flag}</span>
            {language.name}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}